/**
 * Invitation Onboarding Wizard
 *
 * Drives the admin-issued authenticator-enrollment flow using the shared
 * ``useOnboardingFlow`` hook with two customizations:
 *   - a BFF-specific ``OnboardingApiClient`` (``enrollmentInvitationApiClient``)
 *   - ``devicePairingMode: 'always-complete'`` — no device-pairing detour
 *
 * Unlike the user-onboarding wizard, this one does NOT:
 *   - perform a JWT→session exchange on completion
 *   - redirect the user into the portal (the flow ends in a success screen)
 *
 * View components (``FactorSteps``, ``CompleteStepSuccess``, ``FailedStep``,
 * ``Onboarding.*``) are reused from the onboarding feature — they are
 * API-agnostic and drive purely off ``OnboardingState`` + callbacks.
 */

import { useEffect, useRef } from 'react';
import { enrollmentInvitationApiClient } from '@/features/enrollment-invitation/api/enrollment-invitation-api';
// TODO(cross-feature-reuse): lift FailedStep / CompleteStepSuccess / FactorSteps /
// useOnboardingFlow / OnboardingFlowResponse into @/components/factor-enrollment/
// and @/hooks/ so both onboarding and enrollment-invitation consume them without
// the boundaries rule being disabled. Tracked as the follow-up called out in
// ``enrollment-invitation-page.tsx``.
/* eslint-disable boundaries/element-types */
import { FailedStep } from '@/features/onboarding/components/steps/failed-step';
import { FactorSteps } from '@/features/onboarding/components/wizards/shared/factor-steps';
import { useOnboardingFlow } from '@/features/onboarding/hooks/use-onboarding-flow';
import {
  FlowStatus,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';
/* eslint-enable boundaries/element-types */

interface InvitationOnboardingWizardProps {
  initialFlowResponse: OnboardingFlowResponse;
  /**
   * Called with the list of enrolled factors when the flow transitions to
   * COMPLETED. The page is responsible for rendering the terminal screen —
   * we deliberately do NOT render onboarding's ``CompleteStepSuccess`` here
   * because that component offers a "Go to My Apps" button, and our flow
   * does not produce an authenticated session.
   */
  onComplete: (enrolledFactors: string[]) => void;
}

// Sentinel for ``useOnboardingFlow``'s required ``token`` argument. The hook
// forwards it to ``startApi``, which we override with a no-op that ignores
// the argument — the real user is identified by the BFF session cookie on
// every subsequent call.
const UNUSED_TOKEN = 'enrollment-invitation';

export function InvitationOnboardingWizard({
  initialFlowResponse,
  onComplete,
}: InvitationOnboardingWizardProps) {
  const flow = useOnboardingFlow({
    token: UNUSED_TOKEN,
    api: enrollmentInvitationApiClient,
    // The /start call already happened in the page. We pass a no-op startApi
    // that returns the cached initial response; the hook's handleGetStarted
    // kicks off with this seed. We fire the seed immediately on mount below.
    startApi: () => Promise.resolve(initialFlowResponse),
  });

  // Kick off the flow immediately using the cached /start response.
  //
  // React 18 StrictMode double-invokes effects in dev, which for WebAuthn
  // would issue two `/registration/begin` calls and replace the first
  // challenge — the browser then signs a stale challenge, causing "Wrong
  // challenge in response." on `/registration/complete`. A ref guard ensures
  // we call `handleGetStarted` exactly once per component lifetime.
  const hasKickedOffRef = useRef(false);
  useEffect(() => {
    if (hasKickedOffRef.current) {
      return;
    }

    if (!flow.state.hasStarted && !flow.state.isLoading) {
      hasKickedOffRef.current = true;
      void flow.handleGetStarted();
    }
  }, [flow]);

  // When the flow hits COMPLETED, hand control back to the page so it can
  // render the invitation-specific terminal screen. Guarded by a ref so the
  // callback fires exactly once even if the effect re-runs.
  const hasNotifiedCompleteRef = useRef(false);
  useEffect(() => {
    if (
      flow.state.flowStatus === FlowStatus.COMPLETED &&
      !hasNotifiedCompleteRef.current
    ) {
      hasNotifiedCompleteRef.current = true;
      onComplete(flow.state.enrolledFactors);
    }
  }, [flow.state.flowStatus, flow.state.enrolledFactors, onComplete]);

  if (flow.state.flowStatus === FlowStatus.FAILED) {
    return (
      <FailedStep error={flow.state.error} onClearError={flow.clearError} />
    );
  }

  // Note: ``onResend`` / ``canResendSms`` are intentionally omitted. The BFF
  // does not expose a resend endpoint for this flow (``enrollmentInvitationApiClient.resendOtp``
  // is a no-op placeholder), so wiring up the "Resend code" button here would
  // just fire-and-forget and falsely confirm success to the user. If a backend
  // resend endpoint is added later, pass ``flow.resendSmsCode`` /
  // ``flow.canResendSms`` back through.
  return (
    <FactorSteps
      state={flow.state}
      onSubmitFactor={flow.submitFactor}
      onClearError={flow.clearError}
      onChooseAlternative={flow.chooseAlternative}
      onBackToAlternatives={flow.goBackToAlternatives}
      onContinue={flow.completeSecondaryFactors}
    />
  );
}
